import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronsUpDown, Check } from "lucide-react";

// Inline (non-portal) dropdown so the list scrolls normally inside dialogs on mobile.
export default function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder = "Selecione",
  searchPlaceholder = "Buscar...",
  emptyLabel = "Nada encontrado",
  disabled = false,
  wrap = false,
  "data-testid": testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const current = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.sub || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); setQuery(""); }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const pick = (v) => { onChange?.(v); setOpen(false); setQuery(""); };

  return (
    <div ref={rootRef} className="relative min-w-0 w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className={`flex ${wrap ? "min-h-10 py-2" : "h-10"} w-full min-w-0 items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-sm text-left disabled:opacity-60`}
      >
        <span className={`min-w-0 ${wrap ? "break-words whitespace-normal leading-snug" : "truncate"} ${current ? "text-slate-900" : "text-slate-400"}`}>
          {current ? current.label : placeholder}
        </span>
        <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0 ml-2" />
      </button>
      {open && (
        <div className="mt-1 rounded-md border border-slate-200 bg-white shadow-lg" data-testid={testId ? `${testId}-list` : undefined}>
          <div className="p-2 border-b flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full outline-none text-sm bg-transparent"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="max-h-60 overflow-y-auto overscroll-contain py-1" style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500">{emptyLabel}</div>
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-slate-100 flex items-start gap-2 ${o.value === value ? "bg-slate-50" : ""}`}
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
              >
                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${o.value === value ? "text-emerald-600" : "text-transparent"}`} />
                <span className="min-w-0 flex-1">
                  <span className={`block font-medium text-slate-800 ${wrap ? "break-words" : "truncate"}`}>{o.label}</span>
                  {o.sub && <span className={`block text-[11px] text-slate-500 ${wrap ? "break-words" : "truncate"}`}>{o.sub}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
